import React, { ComponentType, JSX, MouseEvent, useEffect, useState } from 'react';

import { Helmet } from 'react-helmet';
import { useLocation } from 'react-router-dom';
import { Link } from '~/lib/react-router-dom';

import KeyboardArrowDown from '@mui/icons-material/KeyboardArrowDown';
import { Box, Button, Container, Menu, MenuItem, Stack } from '@mui/material';

import { expandMenuRoutes, preRegisterSpotlightRoutes } from '~/components/Common/Spotlight/SpotlightProvider';
import { CruiseOffersProvider } from '~/components/Cruises/context/useCruiseOffers';
import LuxPlusPricingPage from '~/components/Cruises/pages/LuxPlusPricing/LuxPlusPricingPage';
import RateCodeCreationPage from '~/components/Cruises/pages/RateCodeCreation/RateCodeCreationPage';
import RateGroupingPage from '~/components/Cruises/pages/RateGrouping/RateGroupingPage';

import BundlesPage from '../Bundles/BundlesListPage';
import CruiseIqPage from '../CruiseIq/CruiseIqPage';
import CruiseLines from '../CruiseLines/CruiseLinesPage';
import DealCrawlerPage from '../DealCrawlerPage/DealCrawlerPage';
import DealPageManagement from '../DealsPage/DealPageManagement';
import DealPageOrdering from '../DealsPage/DealPageOrdering';
import InclusionsPage from '../Inclusions/InclusionsListPage';
import PortsPage from '../Ports/PortsPage';
import RateCodeManagementPage from '../RateCodeManagement/RateCodeManagementPage';
import ShipsPage from '../Ships/ShipsPage';
import SyncOffersPage from '../Tools/SyncOffersPage';

import BookingsPage from './components/Bookings/BookingsPage';
import CabinCategoriesPage from './components/CabinCategories/CabinCategoriesPage';
import CabinCategoriesGroupPage from './components/CabinCategoriesGroup/CabinCategoriesGroupPage';
import CruiseInvalidOffers from './components/CruiseOffers/CruiseInvalidOffers';
import CruiseOfferList from './components/CruiseOffers/CruiseOfferList';
import CruiseSearch from './components/CruiseSearch/CruiseSearch';
import SailingPage from './components/Sailings/SailingPage';

type MenuItemType = {
  name: string;
  href: string;
  component: ComponentType<any> | null;
  includeSearch?: boolean;
  isDisabled?: boolean;
};

export const CruiseOfferMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Flash Offers', href: '/tours/lists/le', component: null },
  { name: 'Cruisebank Offers', href: '/cruises/offers', component: CruiseOfferList, includeSearch: true },
  { name: 'Cruisebank Sailings', href: '/cruises/sailings', component: SailingPage },
  { name: 'Check Invalid Offers', href: '/cruises/invalid-offers', component: CruiseInvalidOffers },
] as const;

export const CruiseCMSMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Cruise Lines', href: '/cruises/cruise-lines', component: CruiseLines },
  { name: 'Ships', href: '/cruises/ships', component: ShipsPage },
  { name: 'Cabin Categories', href: '/cruises/cabin-categories', component: CabinCategoriesPage },
  { name: 'Cabin Categories Groups', href: '/cruises/cabin-categories-groups', component: CabinCategoriesGroupPage },
  { name: 'Ports', href: '/cruises/ports', component: PortsPage },
] as const;

export const RatesPromosMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Rate Code Management', href: '/cruises/rate-code-management', component: RateCodeManagementPage },
  { name: 'Rate Code Creation', href: '/cruises/rate-code-creation', component: RateCodeCreationPage },
  { name: 'Rate Grouping', href: '/cruises/rate-grouping', component: RateGroupingPage },
  { name: 'LuxPlus Pricing', href: '/cruises/luxplus-pricing', component: LuxPlusPricingPage },
  // TODO: Remove menu and old promotions pagehttps://aussiecommerce.atlassian.net/browse/CRUZ-2604
  // { name: 'Promotion Management', href: '/cruises/promotion-management', component: PromotionManagementPage },
  { name: 'Inclusions', href: '/cruises/inclusions', component: InclusionsPage },
  { name: 'Bundles', href: '/cruises/bundles', component: BundlesPage },
  { name: 'Deals Pages', href: '/cruises/deals', component: DealPageManagement },
  { name: 'Deal Page Ordering', href: '/cruises/deals/ordering', component: DealPageOrdering },
  { name: 'Deal Page Crawler', href: '/cruises/deal-crawler', component: DealCrawlerPage },
] as const;

export const BookingsMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Logs', href: '/cruises/booking-logs', component: BookingsPage },
] as const;

export const CruiseIqMenu: ReadonlyArray<MenuItemType> = [
  { name: 'CruiseIQ', href: '/cruises/cruise-iq', component: CruiseIqPage },
] as const;

export const ToolsMenu: ReadonlyArray<MenuItemType> = [
  { name: 'Sync offers', href: '/cruises/sync-offers', component: SyncOffersPage },
] as const;

const NavigationMenu = [
  { name: 'Offers', menu: CruiseOfferMenu },
  { name: 'Cruise CMS', menu: CruiseCMSMenu },
  { name: 'Rates & Promos', menu: RatesPromosMenu },
  { name: 'Bookings', menu: BookingsMenu },
];

const allMenuItems = [...NavigationMenu.flatMap((item) => item.menu), ...CruiseIqMenu, ...ToolsMenu];

const setDefaultItem = (locationPathname: string) => {
  const defaultItem = CruiseOfferMenu[1];

  const locationItem = allMenuItems.find((item) => item.href === locationPathname);
  return locationItem ?? defaultItem;
};

preRegisterSpotlightRoutes(
  expandMenuRoutes('Cruises', [
    ...CruiseOfferMenu,
    ...CruiseCMSMenu,
    ...RatesPromosMenu,
    ...BookingsMenu,
    ...CruiseIqMenu,
    ...ToolsMenu,
  ]),
);

function CruisesPage(): JSX.Element {
  const location = useLocation();
  const defaultItem = setDefaultItem(location.pathname);
  const [currentItem, setCurrentItem] = useState(defaultItem);
  const [currentMenu, setCurrentMenu] = useState(null);
  const title = `CRUISE - ${currentItem.name}`;
  const [open, setOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState<null | HTMLElement>(null);
  const handleNavigation = (event: MouseEvent<HTMLElement>, menu: ReadonlyArray<MenuItemType>) => {
    setAnchorEl(event.currentTarget);
    setCurrentMenu(menu);
    setOpen(true);
  };
  const handleMenuClose = () => {
    setOpen(false);
    setAnchorEl(null);
  };
  const handleMenuClick = (item) => {
    setCurrentItem(item);
    handleMenuClose();
  };
  useEffect(() => {
    setCurrentItem(defaultItem);
  }, [defaultItem]);

  return (
    <CruiseOffersProvider>
      <Container maxWidth="xl">
        <Stack direction="row" spacing={1}>
          {NavigationMenu.map((item) => (
            <Button
              key={item.name}
              onClick={(event) => handleNavigation(event, item.menu)}
              endIcon={<KeyboardArrowDown />}
            >
              {item.name}
            </Button>
          ))}

          <Button component={Link} to={CruiseIqMenu[0].href}>
            CruiseIQ
          </Button>

          <Button onClick={(event) => handleNavigation(event, ToolsMenu)} endIcon={<KeyboardArrowDown />}>
            Tools
          </Button>

          <Menu open={open} anchorEl={anchorEl} onClose={handleMenuClose}>
            {currentMenu &&
              currentMenu
                .filter((item) => !item.isDisabled)
                .map((item) => (
                  <MenuItem key={item.name} onClick={() => handleMenuClick(item)} to={item.href} component={Link}>
                    {item.name}
                  </MenuItem>
                ))}
          </Menu>
        </Stack>

        <Helmet>
          <title>{title}</title>
        </Helmet>

        {currentItem.includeSearch && <CruiseSearch />}

        <Box mt={3}>
          <Box component={currentItem?.component} />
        </Box>
      </Container>
    </CruiseOffersProvider>
  );
}

export default CruisesPage;
